import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "owner" });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Owner '${form.email}' created.`);
      setForm({ email: "", name: "", password: "", role: "owner" });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not create user."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <DashboardShell>
      <div data-testid="admin-users">
        <div className="overline text-sg-gold-deep">USERS</div>
        <h1 className="font-display text-4xl text-sg-navy mt-1">Owner <span className="font-serif-italic text-sg-gold-deep">accounts</span></h1>
        <p className="text-sg-mute mt-2 max-w-2xl">Only owner-role users are listed here. Agents live on <strong>Registered Agents</strong>.</p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-sg-line rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sg-cream-soft text-sg-mute text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="px-4 py-12 text-center text-sg-mute"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-12 text-center text-sg-mute">No owners yet.</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.user_id} className="border-t border-sg-line" data-testid={`admin-user-row-${u.user_id}`}>
                        <td className="px-4 py-3 font-medium text-sg-navy">{u.name}</td>
                        <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sg-cream-soft border border-sg-line text-sg-gold-deep">
                            <ShieldCheck className="w-3 h-3" /> {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <form onSubmit={onCreate} className="bg-white border border-sg-line rounded-2xl p-6" data-testid="admin-user-create-form">
            <div className="overline text-sg-gold-deep">ADD OWNER</div>
            <h2 className="font-display text-2xl text-sg-navy mt-1">New user</h2>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="u-name" className="text-xs uppercase tracking-wider text-sg-mute">Name</Label>
                <Input id="u-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-sg-cream-soft" data-testid="admin-user-name" />
              </div>
              <div>
                <Label htmlFor="u-email" className="text-xs uppercase tracking-wider text-sg-mute">Email</Label>
                <Input id="u-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-sg-cream-soft" data-testid="admin-user-email" />
              </div>
              <div>
                <Label htmlFor="u-pw" className="text-xs uppercase tracking-wider text-sg-mute">Password (8+)</Label>
                <Input id="u-pw" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-sg-cream-soft" data-testid="admin-user-password" />
              </div>
            </div>
            <Button type="submit" disabled={creating} className="mt-5 w-full rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-11" data-testid="admin-user-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create owner
            </Button>
          </form>
        </div>
      </div>
    </DashboardShell>
  );
}
